'use client';

import { useCallback, useEffect, useRef, useState, Suspense } from 'react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { motion } from 'framer-motion';
import { AtSign, KeyRound, Eye, EyeOff, ArrowRight, Loader2 } from 'lucide-react';
import { EnthiosHubLoader } from '@/components/ui/EnthiosHubLoader';
import {
  clearAdminTwoFactorLoginChallenge,
  isAdminAccessDeniedError,
  isAdminLoginRequiredError,
  isAdminTwoFactorSetupRequired,
  isAdminTwoFactorChallengeExpiredError,
  isAdminTwoFactorLoginRequiredError,
  persistAdminTwoFactorLoginChallenge,
  readAdminTwoFactorLoginChallenge,
  useAuthStore,
} from '@/lib/auth-store';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { AdminSecurityPanel } from '@/components/access/AdminSecurityPanel';
import { authApi, maintenanceApi } from '@/lib/api';
import { getApiBaseUrl, shouldUseAdminLogin } from '@/lib/env';
import { traceLogin } from '@/lib/login-redirect-trace';
import { appleSignInEnabled, firebaseEnabled } from '@/lib/firebase-config';
import { getEffectiveRole, isAdminRole } from '@/lib/rbac';
import {
  completeEnthiosGoogleSession,
  getSocialAuthErrorMessage,
  logGoogleAuthStep,
  recoverFirebaseUserAfterPopupClose,
  resolveAfterSocialLogin,
  shouldFallbackToRedirect,
} from '@/lib/social-login';

function LoginContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const isAdminPortalLogin = shouldUseAdminLogin();
  const loginReason = searchParams?.get('reason');
  const redirectAfterLogin = searchParams?.get('redirect')?.trim() || undefined;
  const authUser = useAuthStore((state) => state.user);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const authChecking = useAuthStore((state) => state.authChecking);
  const sessionNotice =
    loginReason === 'logged_out'
      ? 'You have been successfully signed out.'
      : loginReason === 'two_factor'
        ? 'Enter your email and password below, then provide your authenticator code on the next step.'
        : loginReason === 'access_denied'
          ? 'This account does not have admin portal access. Sign in with an authorized admin email and password.'
          : loginReason === 'session_expired'
            ? 'Your session expired for your security. Please sign in again to continue.'
            : '';
  const [mounted, setMounted] = useState(false);
  const [form, setForm] = useState({ email: '', password: '' });
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);
  const loginSubmittingRef = useRef(false);
  const twoFactorSubmittingRef = useRef(false);
  const lastRejectedTwoFactorCodeRef = useRef<string | null>(null);
  const [lastRejectedTwoFactorCode, setLastRejectedTwoFactorCode] = useState<string | null>(null);
  const [twoFactorChallenge, setTwoFactorChallenge] = useState<{
    challengeToken: string;
    email?: string;
  } | null>(() => readAdminTwoFactorLoginChallenge());
  const [twoFactorSetupRequired, setTwoFactorSetupRequired] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const socialAuthEnabled = firebaseEnabled() && !isAdminPortalLogin;
  const showAppleSignIn = socialAuthEnabled && appleSignInEnabled;

  useEffect(() => {
    setMounted(true);
    setForm({ email: '', password: '' });
    setRememberMe(false);
  }, []);

  useEffect(() => {
    if (!isAuthenticated || loginReason === 'logged_out' || loginReason === 'session_expired') {
      setForm({ email: '', password: '' });
      setRememberMe(false);
    }
  }, [isAuthenticated, loginReason]);

  const navigateAfterLogin = useCallback(async (targetPath: string) => {
    const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
    traceLogin('navigateAfterLogin CALLED', { targetPath, currentPath });
    
    let destination = targetPath;
    if (!destination || destination === '/login' || (typeof window !== 'undefined' && destination === window.location.pathname)) {
      const user = useAuthStore.getState().user;
      const role = user ? getEffectiveRole(user) : '';
      if (role === 'admin' || role === 'super_admin' || user?.master_admin) {
        destination = '/dashboard/admin';
      } else if (role === 'jury' || role === 'judge') {
        destination = '/jury';
      } else if (role === 'organizer' || role === 'organization_admin') {
        destination = '/dashboard/organizer';
      } else {
        destination = '/dashboard';
      }
    }

    // Check maintenance mode: non-admin users cannot access dashboards while maintenance is active
    const userRole = getEffectiveRole(useAuthStore.getState().user);
    if (!isAdminRole(userRole)) {
      try {
        const res = await maintenanceApi.status();
        if (res.data?.enabled) {
          traceLogin('MAINTENANCE REDIRECT EXECUTED', { targetPath: destination, destination: '/maintenance' });
          window.location.href = '/maintenance';
          return;
        }
      } catch {
        // Continue normally
      }
    }

    traceLogin('WINDOW.LOCATION.HREF EXECUTED', { destination, currentPath });
    window.location.href = destination;
  }, []);

  useEffect(() => {
    if (!mounted || authChecking || !isAuthenticated || !authUser) return;
    // Never wipe / skip a pending admin 2FA challenge because of a stale
    // persisted session or a racing checkAuth success.
    if (twoFactorChallenge || readAdminTwoFactorLoginChallenge()) return;
    // If the user just logged out and landed here, don't auto-redirect.
    if (loginReason === 'session_expired' || loginReason === 'logged_out') {
      useAuthStore.getState().clearSessionLocal();
      return;
    }
    if (isAdminPortalLogin && isAdminTwoFactorSetupRequired(authUser)) {
      setTwoFactorSetupRequired(true);
      clearAdminTwoFactorLoginChallenge();
      setTwoFactorChallenge(null);
      setTwoFactorCode('');
      return;
    }
    if (twoFactorSetupRequired && isAdminPortalLogin) {
      setTwoFactorSetupRequired(false);
    }
    clearAdminTwoFactorLoginChallenge();
    setTwoFactorChallenge(null);
    setTwoFactorCode('');
    const state = useAuthStore.getState();
    navigateAfterLogin(state.getRedirectForUser(authUser, { redirectPath: redirectAfterLogin }));
  }, [authChecking, authUser, isAdminPortalLogin, isAuthenticated, loginReason, mounted, navigateAfterLogin, redirectAfterLogin, twoFactorChallenge, twoFactorSetupRequired]);

  // Handle Google redirect result on page load (fires when returning from signInWithRedirect)
  useEffect(() => {
    if (!mounted) return;
    void (async () => {
      try {
        if (!firebaseEnabled()) return;
        const { getFirebaseAuth, googleProvider } = await import('@/lib/firebase');
        const { getRedirectResult } = await import('firebase/auth');
        const firebaseAuth = getFirebaseAuth();
        if (!firebaseAuth) return;
        const result = await getRedirectResult(firebaseAuth);
        if (!result) return; // No pending redirect — normal page load
        const firebaseUser = result.user;
        if (!firebaseUser) return;
        setGoogleLoading(true);
        setError('');
        logGoogleAuthStep('redirect_result_received', { has_user: true });
        const destination = await completeEnthiosGoogleSession({
          firebaseUser,
          firebaseLogin: authApi.firebaseLogin,
          checkAuth: (opts) => useAuthStore.getState().checkAuth(opts),
          getAuthUser: () => useAuthStore.getState().user,
          apiHost: (() => {
            try {
              return new URL(getApiBaseUrl(), window.location.origin).hostname;
            } catch {
              return getApiBaseUrl();
            }
          })(),
          redirectPath: redirectAfterLogin,
        });
        navigateAfterLogin(destination);
      } catch (err: any) {
        const code = String(err?.code || '');
        // auth/no-auth-event means no redirect happened — silently ignore
        if (code === 'auth/no-auth-event' || code === 'auth/null-user') return;
        setError(getSocialAuthErrorMessage(err, 'Google'));
      } finally {
        setGoogleLoading(false);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mounted]);

  const handleGoogleSignIn = async () => {
    setError('');
    if (isAdminPortalLogin) {
      setError('Google sign-in is not available on the admin portal. Use your admin email and password.');
      return;
    }
    setGoogleLoading(true);
    try {
      if (!firebaseEnabled()) throw new Error('Google authentication is not configured.');
      const { getFirebaseAuth, googleProvider } = await import('@/lib/firebase');
      const { signInWithPopup, signInWithRedirect } = await import('firebase/auth');
      const firebaseAuth = getFirebaseAuth();
      if (!firebaseAuth) throw new Error('Google authentication is temporarily unavailable.');

      logGoogleAuthStep('popup_started', {
        api_host: (() => {
          try {
            return new URL(getApiBaseUrl(), window.location.origin).hostname;
          } catch {
            return getApiBaseUrl();
          }
        })(),
      });

      let firebaseUser = null as Awaited<ReturnType<typeof signInWithPopup>>['user'] | null;
      try {
        const credential = await signInWithPopup(firebaseAuth, googleProvider);
        firebaseUser = credential.user ?? firebaseAuth.currentUser;
        logGoogleAuthStep('popup_completed', { has_user: Boolean(firebaseUser) });
      } catch (popupErr: any) {
        const code = String(popupErr?.code || '');
        logGoogleAuthStep('popup_rejected', { code: code || 'unknown' });
        if (shouldFallbackToRedirect(code)) {
          await signInWithRedirect(firebaseAuth, googleProvider);
          return;
        }
        if (code === 'auth/popup-closed-by-user') {
          firebaseUser = await recoverFirebaseUserAfterPopupClose(() => firebaseAuth.currentUser);
          if (!firebaseUser) throw popupErr;
          logGoogleAuthStep('popup_user_recovered_after_close', { has_user: true });
        } else {
          throw popupErr;
        }
      }

      if (!firebaseUser) throw new Error('Google login failed. Please try again.');
      const destination = await completeEnthiosGoogleSession({
        firebaseUser,
        firebaseLogin: authApi.firebaseLogin,
        checkAuth: (opts) => useAuthStore.getState().checkAuth(opts),
        getAuthUser: () => useAuthStore.getState().user,
        apiHost: (() => {
          try {
            return new URL(getApiBaseUrl(), window.location.origin).hostname;
          } catch {
            return getApiBaseUrl();
          }
        })(),
        redirectPath: redirectAfterLogin,
      });
      navigateAfterLogin(destination);
    } catch (err: any) {
      setError(getSocialAuthErrorMessage(err, 'Google'));
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleAppleSignIn = async () => {
    setError('');
    if (isAdminPortalLogin) {
      setError('Apple sign-in is not available on the admin portal. Use your admin email and password.');
      return;
    }
    if (!showAppleSignIn) {
      setError('Apple sign-in is currently unavailable in this environment.');
      return;
    }
    setAppleLoading(true);
    try {
      if (!firebaseEnabled()) throw new Error('Apple authentication is not configured.');
      const { appleProvider, getFirebaseAuth } = await import('@/lib/firebase');
      const { signInWithPopup } = await import('firebase/auth');
      const firebaseAuth = getFirebaseAuth();
      if (!firebaseAuth) throw new Error('Apple authentication is temporarily unavailable.');
      await signInWithPopup(firebaseAuth, appleProvider);
      const firebaseUser = firebaseAuth.currentUser;
      if (!firebaseUser) throw new Error('Apple login failed. Please try again.');
      const token = await firebaseUser.getIdToken(true);
      const profile = { name: firebaseUser.displayName || undefined, email: firebaseUser.email || undefined };
      await authApi.firebaseLogin(token, profile);
      await useAuthStore.getState().checkAuth();
      const state = useAuthStore.getState();
      navigateAfterLogin(resolveAfterSocialLogin(state.user, { redirectPath: redirectAfterLogin }));
    } catch (err: any) {
      setError(getSocialAuthErrorMessage(err, 'Apple'));
    } finally {
      setAppleLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (loginSubmittingRef.current || isLoading || googleLoading || appleLoading) return;
    setError('');

    const formElement = e.currentTarget;
    const formData = new FormData(formElement);
    const formEmail = (formData.get('email') as string) || form.email;
    const formPassword = (formData.get('password') as string) || form.password;

    const trimmedEmail = (formEmail || '').trim();
    if (!trimmedEmail || !formPassword) {
      setError('Please enter your email and password.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setError('Please enter a valid email address.');
      return;
    }
    loginSubmittingRef.current = true;
    setIsLoading(true);
    try {
      const state = useAuthStore.getState();
      const redirectPath = await state.login(trimmedEmail, formPassword, {
        redirectPath: redirectAfterLogin,
        remember: rememberMe,
        adminLogin:
          isAdminPortalLogin ||
          Boolean(twoFactorChallenge?.challengeToken) ||
          Boolean(readAdminTwoFactorLoginChallenge()?.challengeToken),
      });
      if (
        isAdminPortalLogin &&
        (redirectPath.includes('/setup-2fa') || isAdminTwoFactorSetupRequired(useAuthStore.getState().user))
      ) {
        setTwoFactorSetupRequired(true);
        setTwoFactorChallenge(null);
        clearAdminTwoFactorLoginChallenge();
        setTwoFactorCode('');
        setError('');
        loginSubmittingRef.current = false;
        return;
      }
      navigateAfterLogin(redirectPath);
    } catch (err: any) {
      if (isAdminTwoFactorLoginRequiredError(err)) {
        const challenge = {
          challengeToken: err.challengeToken,
          email: typeof err.pendingUser?.email === 'string' ? err.pendingUser.email : trimmedEmail,
        };
        persistAdminTwoFactorLoginChallenge(challenge.challengeToken, challenge.email);
        if (typeof window !== 'undefined') {
          const next = new URL(window.location.href);
          next.searchParams.set('admin', '1');
          next.searchParams.set('reason', 'two_factor');
          window.history.replaceState({}, '', `${next.pathname}${next.search}${next.hash}`);
        }
        setTwoFactorChallenge(challenge);
        setTwoFactorCode('');
        setLastRejectedTwoFactorCode(null);
        lastRejectedTwoFactorCodeRef.current = null;
        setError('');
        // Clear any stale checkAuth outage banner — 2FA challenge is success, not failure.
        useAuthStore.setState({ authError: null, authChecking: false, isAuthenticated: false, user: null });
        loginSubmittingRef.current = false;
        return;
      }
      if (isAdminAccessDeniedError(err)) {
        router.replace('/login?reason=access_denied');
        setError('This account does not have admin portal access. Use regular sign-in or an authorized admin account.');
        loginSubmittingRef.current = false;
        return;
      }
      if (isAdminLoginRequiredError(err)) {
        const redirect =
          typeof err.redirect === 'string' && err.redirect.startsWith('/')
            ? err.redirect
            : '/login?admin=1';
        window.location.href = redirect;
        // Expected portal switch — do not surface as a login failure.
        loginSubmittingRef.current = false;
        return;
      }
      setError(String(err?.message || 'Sign in failed. Please try again.'));
    } finally {
      loginSubmittingRef.current = false;
      setIsLoading(false);
    }
  };

  const handleTwoFactorSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (twoFactorSubmittingRef.current || isLoading) return;
    if (!twoFactorChallenge) return;
    const code = twoFactorCode.trim();
    if (!code) {
      setError('Enter the 6-digit code from your authenticator app.');
      return;
    }
    const isTotp = /^\d{6}$/.test(code);
    const isRecoveryCode = /^[a-z0-9]{4}-?[a-z0-9]{4}$/i.test(code);
    if (!isTotp && !isRecoveryCode) {
      setError('Enter a 6-digit authenticator code or a recovery code like ABCD-1234.');
      return;
    }
    if (lastRejectedTwoFactorCodeRef.current === code) {
      setError('That code was already rejected. Enter the newest code from your authenticator app.');
      return;
    }
    twoFactorSubmittingRef.current = true;
    setError('');
    setIsLoading(true);
    try {
      const state = useAuthStore.getState();
      const redirectPath = await state.completeAdminTwoFactorLogin(
        twoFactorChallenge.challengeToken,
        code,
        { redirectPath: redirectAfterLogin }
      );
      lastRejectedTwoFactorCodeRef.current = null;
      setLastRejectedTwoFactorCode(null);
      setTwoFactorChallenge(null);
      clearAdminTwoFactorLoginChallenge();
      navigateAfterLogin(redirectPath);
    } catch (err: any) {
      if (isAdminTwoFactorChallengeExpiredError(err)) {
        setTwoFactorChallenge(null);
        setTwoFactorCode('');
        lastRejectedTwoFactorCodeRef.current = null;
        setLastRejectedTwoFactorCode(null);
        clearAdminTwoFactorLoginChallenge();
        setError(err.message);
        return;
      }
      if (isAdminAccessDeniedError(err)) {
        router.replace('/login?reason=access_denied');
        setError('This account does not have admin portal access. Use regular sign-in or an authorized admin account.');
        return;
      }
      lastRejectedTwoFactorCodeRef.current = code;
      setLastRejectedTwoFactorCode(code);
      setError(typeof err === 'string' ? err : err?.message || 'Verification failed.');
    } finally {
      twoFactorSubmittingRef.current = false;
      setIsLoading(false);
    }
  };

  const twoFactorCodeValue = twoFactorCode.trim();
  const sameRejectedTwoFactorCode =
    Boolean(twoFactorCodeValue) && lastRejectedTwoFactorCode === twoFactorCodeValue;
  const loginPanelWidth = twoFactorSetupRequired
    ? 'max-w-3xl'
    : 'max-w-md';

  if (!mounted) {
    return <EnthiosHubLoader variant="fullscreen" showDelayMs={0} />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10 relative bg-[#F9F7F0]">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className={`w-full ${loginPanelWidth}`}
      >
        <div className="text-center mb-8">
          <Link href="/" className="inline-flex items-center justify-center mb-6">
            <img
              src="/logo-enthioshub.png"
              alt="EnthiosHub"
              width={384}
              height={119}
              className="h-auto w-[min(320px,85vw)] object-contain"
              decoding="async"
            />
          </Link>
          <h1 className="text-3xl sm:text-4xl font-bold text-[#2D3536] mb-2 tracking-tight">
            {twoFactorSetupRequired ? 'Set up two-factor authentication' : twoFactorChallenge ? 'Verify your identity' : 'Welcome Back'}
          </h1>
          <p className="text-[#6E7B68] font-medium text-sm sm:text-base">
            {twoFactorSetupRequired
              ? 'Scan the QR code with your authenticator app, then enter the 6-digit code from your phone.'
              : twoFactorChallenge
              ? `Enter the code from your authenticator app${twoFactorChallenge.email ? ` for ${twoFactorChallenge.email}` : ''}.`
              : isAdminPortalLogin
                ? 'Sign in to the Admin Portal'
                : 'Sign in to EnthiosHub'}
          </p>
        </div>

        <div className="bg-[#FDFBF7] border border-[#E5E2D9] rounded-3xl p-6 sm:p-10 shadow-lg relative overflow-hidden">
          {sessionNotice && !error && !twoFactorChallenge && !twoFactorSetupRequired && (
            <div
              className={`mb-6 p-4 rounded-xl text-sm font-medium ${
                loginReason === 'access_denied'
                  ? 'bg-red-50 border border-red-100 text-red-700'
                  : 'bg-amber-50 border border-amber-100 text-amber-900'
              }`}
            >
              {sessionNotice}
            </div>
          )}
          {error && (
            <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-100 text-red-600 text-sm font-medium flex gap-3 items-center">
              <div className="w-2 h-2 rounded-full bg-red-500 shrink-0" />
              {error}
            </div>
          )}

          {twoFactorSetupRequired ? (
            <div className="space-y-5">
              <div className="rounded-xl border border-[#5F6F52]/20 bg-[#5F6F52]/10 p-4 text-sm text-[#2D3536]">
                <p className="font-semibold">Authenticator setup required</p>
                <p className="mt-1 text-[#6E7B68]">
                  Use Google Authenticator, Authy, Microsoft Authenticator, or any TOTP app. After scanning, type the current 6-digit code shown on your phone.
                </p>
              </div>
              <AdminSecurityPanel view="two-factor" />
              <button
                type="button"
                onClick={() => {
                  setTwoFactorSetupRequired(false);
                  setTwoFactorChallenge(null);
                  clearAdminTwoFactorLoginChallenge();
                  useAuthStore.getState().clearSessionLocal();
                  setError('');
                }}
                className="w-full text-sm font-medium text-[#6E7B68] hover:text-[#2D3536]"
              >
                Use a different admin account
              </button>
            </div>
          ) : twoFactorChallenge ? (
            <div className="space-y-5">
              <form onSubmit={handleTwoFactorSubmit} className="space-y-5">
                <Input
                  label="Authenticator code"
                  type="text"
                  inputMode="numeric"
                  value={twoFactorCode}
                  onChange={(e) => {
                    const nextValue = e.target.value;
                    setTwoFactorCode(nextValue);
                    if (lastRejectedTwoFactorCode !== null && nextValue.trim() !== lastRejectedTwoFactorCode) {
                      setLastRejectedTwoFactorCode(null);
                      lastRejectedTwoFactorCodeRef.current = null;
                    }
                  }}
                  placeholder="6-digit code or recovery code"
                  leftIcon={<KeyRound className="w-4 h-4" />}
                  required
                  autoFocus
                />
                <Button
                  type="submit"
                  fullWidth
                  size="lg"
                  loading={isLoading}
                  disabled={isLoading || sameRejectedTwoFactorCode}
                  className="bg-[#5F6F52] hover:bg-[#4E5D42] text-white font-semibold rounded-xl h-12"
                >
                  Verify and continue
                </Button>
              </form>

              <button
                type="button"
                onClick={() => {
                  setTwoFactorChallenge(null);
                  clearAdminTwoFactorLoginChallenge();
                  setTwoFactorCode('');
                  setLastRejectedTwoFactorCode(null);
                  lastRejectedTwoFactorCodeRef.current = null;
                  setError('');
                }}
                className="w-full text-sm font-medium text-[#6E7B68] hover:text-[#2D3536]"
              >
                Back to sign in
              </button>

              <Link
                href={twoFactorChallenge?.email ? `/2fa/reset-request?email=${encodeURIComponent(twoFactorChallenge.email)}` : '/2fa/reset-request'}
                className="w-full text-sm font-medium text-[#6E7B68] hover:text-[#2D3536] text-center block transition-colors"
              >
                Lost your authenticator? Re-authenticate
              </Link>
            </div>
          ) : (
          <form onSubmit={handleSubmit} className="space-y-5" autoComplete="on">
            <Input
              label="Email address"
              type="email"
              name="email"
              value={form.email}
              onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))}
              placeholder="name@email.com"
              autoComplete="email"
              leftIcon={<AtSign className="w-4 h-4" />}
              required
            />

            <Input
              label="Password"
              type={showPassword ? 'text' : 'password'}
              name="password"
              value={form.password}
              onChange={(e) => setForm(p => ({ ...p, password: e.target.value }))}
              placeholder="••••••••"
              autoComplete="current-password"
              leftIcon={<KeyRound className="w-4 h-4" />}
              rightIcon={
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="text-[#5F6F52]/70 hover:text-[#2D3536] transition-colors p-1 rounded-md"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                </button>
              }
              required
            />

            <div className="flex items-center justify-between text-sm flex-wrap gap-2 pt-1">
              <label className="flex items-center gap-2 text-[#6E7B68] cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(event) => setRememberMe(event.target.checked)}
                  className="w-4 h-4 rounded border-[#E2DFD5] accent-[#5F6F52] text-[#5F6F52] focus:ring-[#5F6F52]/20"
                />
                <span>Remember me</span>
              </label>
              <Link href="/forgot-password" className="text-[#5F6F52] font-semibold hover:underline transition-all">
                Forgot password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full h-12 bg-[#5F6F52] hover:bg-[#4E5D42] text-white font-semibold rounded-xl text-base transition-all flex items-center justify-center gap-2 shadow-xs disabled:opacity-50 cursor-pointer"
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <span>Sign in</span>
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>

            {socialAuthEnabled && (
              <div className="pt-2 space-y-3">
                <button
                  type="button"
                  onClick={handleGoogleSignIn}
                  disabled={googleLoading || isLoading}
                  className="w-full h-12 flex items-center justify-center gap-3 rounded-xl border border-[#E2DFD5] bg-[#F9F7F0] hover:bg-[#F2EFE6] transition-all font-semibold text-[#2D3536] disabled:opacity-50 text-sm shadow-xs"
                >
                  {googleLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin text-[#5F6F52]" />
                  ) : (
                    <>
                      <svg className="w-5 h-5" viewBox="0 0 24 24">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                      </svg>
                      <span>Continue with Google</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </form>
          )}

          {!twoFactorChallenge && !twoFactorSetupRequired && !isAdminPortalLogin && (
          <p className="text-center text-[#6E7B68] text-sm mt-8">
            Don't have an account?{' '}
            <Link href="/register" className="text-[#5F6F52] font-bold hover:underline">
              Create one
            </Link>
          </p>
          )}
        </div>
      </motion.div>
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={<EnthiosHubLoader variant="fullscreen" showDelayMs={0} />}>
      <LoginContent />
    </Suspense>
  );
}
